import React, { useRef, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Modal,
  Form,
  Select,
  Space,
  Input,
  Menu,
  Dropdown,
  Row,
  Col,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchProjectUsers,
  removeUser,
  updateUserRoleVendor,
} from "../services/projectsAPI";
import { fetchVendors } from "../services/vendorsAPI";
import { useAuth } from "../hooks/useAuth";
import CreateUserModal from "../components/CreateUserModal";
import AddUsersFromDirectoryModal from "../components/AddUsersFromDirectoryModal";
import Highlighter from "react-highlight-words";
import {
  filterOption,
  formatPhoneNumber,
  handlePhoneNumberKeyDown,
} from "../utils/textUtils";
import { exportUsersToPDF } from "../utils/pdfUtils";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";

const { Option } = Select;

const Users = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const projectName = selectedProject.project_name;
  const [isCreateUserModalVisible, setIsCreateUserModalVisible] =
    useState(false);
  const [isAddUsersFromDirectoryModalVisible, setIsAddUsersFromDirectoryModalVisible] =
    useState(false);
  const [isUpdateUserModalVisible, setIsUpdateUserModalVisible] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [updateForm] = Form.useForm();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize('users'),
    total: 0,
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const {
    data: users,
    isLoading: isUsersLoading,
    isError: isUsersError,
    error: usersError,
  } = useQuery({
    queryKey: [
      "projectUsers",
      projectId,
      pagination.current,
      pagination.pageSize,
      columnFilters,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchProjectUsers(
        projectId,
        pagination.current,
        pagination.pageSize,
        columnFilters,
        sortField,
        sortOrder
      ),
    enabled: !!projectId,
    keepPreviousData: true,
  });

  const {
    data: vendors,
    isLoading: isVendorsLoading,
    isError: isVendorsError,
    error: vendorsError,
  } = useQuery({
    queryKey: ["vendors", projectId, 1, 1000],
    queryFn: () => fetchVendors(projectId, 1, 1000),
    enabled: !!projectId,
  });

  const deleteMutation = useMutation({
    mutationFn: (userId) => removeUser(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries(["projectUsers", projectId]);
      notification.success({ message: "User removed successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to remove user",
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) =>
      updateUserRoleVendor(projectId, selectedUser.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["projectUsers", projectId]);
      notification.success({ message: "User updated successfully" });
      setIsUpdateUserModalVisible(false);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update user",
        description: error.message,
      });
    },
  });

  const handleExportPDF = async () => {
    setIsExportingPDF(true);
    try {
      const exportPageSize = 1000;
      const firstPage = await fetchProjectUsers(
        projectId,
        1,
        exportPageSize,
        columnFilters,
        sortField,
        sortOrder
      );

      const totalUsers = firstPage?.total || 0;
      let usersData = firstPage?.data || [];
      const effectivePageSize =
        (firstPage?.data && firstPage.data.length > 0
          ? firstPage.data.length
          : exportPageSize);
      const totalPages = Math.ceil(totalUsers / effectivePageSize);

      if (totalPages > 1) {
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page += 1) {
          pageRequests.push(
            fetchProjectUsers(
              projectId,
              page,
              exportPageSize,
              columnFilters,
              sortField,
              sortOrder
            )
          );
        }

        const remainingPages = await Promise.all(pageRequests);
        usersData = usersData.concat(
          remainingPages.flatMap((pageResult) => pageResult?.data || [])
        );
      }

      if (usersData.length === 0) {
        notification.warning({ message: "No data to export" });
        return;
      }

      exportUsersToPDF(usersData, projectName, { preserveOrder: !!sortField });
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    } finally {
      setIsExportingPDF(false);
    }
  };

  if (isUsersError) {
    notification.error({
      message: "Failed to load users",
      description: usersError.message,
    });
  }

  if (isVendorsError) {
    notification.error({
      message: "Failed to load vendors",
      description: vendorsError.message,
    });
  }

  const handleRemoveUserClick = (userId) => {
    deleteMutation.mutate(userId);
  };

  const handleUpdateUserClick = (user) => {
    setSelectedUser(user);
    updateForm.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      vendor_id: user.vendor_id,
      phone_number: formatPhoneNumber(user.phone_number),
      notes: user.notes,
    });
    setIsUpdateUserModalVisible(true);
  };

  const handleUpdateUser = (values) => {
    updateMutation.mutate(values);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    filteredValue: columnFilters[dataIndex] ? [columnFilters[dataIndex]] : null,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnSortOrder = (key) =>
    sortField === key ? (sortOrder === "desc" ? "descend" : "ascend") : null;

  const columns = [
    {
      title: "First Name",
      dataIndex: "first_name",
      key: "first_name",
      sorter: true,
      sortOrder: getColumnSortOrder("first_name"),
      ...getColumnSearchProps("first_name"),
    },
    {
      title: "Last Name",
      dataIndex: "last_name",
      key: "last_name",
      sorter: true,
      sortOrder: getColumnSortOrder("last_name"),
      ...getColumnSearchProps("last_name"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Vendor Name",
      dataIndex: "vendor_name",
      key: "vendor_name",
      sorter: true,
      sortOrder: getColumnSortOrder("vendor_name"),
      ...getColumnSearchProps("vendor_name"),
    },
    {
      title: "Role",
      dataIndex: "formatted_role",
      key: "formatted_role",
      ...getColumnSearchProps("formatted_role"),
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      ...getColumnSearchProps("phone_number"),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      ...getColumnSearchProps("notes"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        record.role !== "owner" && (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="1"
                  onClick={() => handleUpdateUserClick(record)}
                >
                  Update
                </Menu.Item>
                <Menu.Item key="2">
                  <Popconfirm
                    title="Are you sure you want to delete this user?"
                    onConfirm={() => handleRemoveUserClick(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    Delete
                  </Popconfirm>
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button icon={<EditOutlined />} />
          </Dropdown>
        ),
    },
  ];

  const handleTableChange = (newPagination, filters, sorter) => {
    const nextSortField = sorter?.order ? sorter.columnKey : null;
    const nextSortOrder =
      sorter?.order === "ascend" ? "asc" : sorter?.order === "descend" ? "desc" : null;
    const isSortChanged = nextSortField !== sortField || nextSortOrder !== sortOrder;
    setSortField(nextSortField);
    setSortOrder(nextSortOrder);
    setPagination(isSortChanged ? { ...newPagination, current: 1 } : newPagination);
    savePreferredPageSize(newPagination.pageSize, 'users');
    // save filters to state for later use
    const mappedFilters = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key] && filters[key][0]) {
        mappedFilters[key] = filters[key][0];
      }
    });
    setColumnFilters(mappedFilters);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Project Users</h1>
      <div className="flex gap-4 mb-4">
        <Button
          type="primary"
          onClick={() => setIsCreateUserModalVisible(true)}
        >
          Add User
        </Button>
        <Button
          type="default"
          onClick={() => setIsAddUsersFromDirectoryModalVisible(true)}
        >
          Add Users from Directory
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExportPDF}
          loading={isExportingPDF}
          disabled={isExportingPDF}
        >
          Export PDF
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={users?.data}
        rowKey="id"
        loading={isUsersLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: users?.total,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <CreateUserModal
        visible={isCreateUserModalVisible}
        onClose={() => setIsCreateUserModalVisible(false)}
      />
      <AddUsersFromDirectoryModal
        visible={isAddUsersFromDirectoryModalVisible}
        onClose={() => setIsAddUsersFromDirectoryModalVisible(false)}
      />
      <Modal
        title="Update User"
        open={isUpdateUserModalVisible}
        onCancel={() => setIsUpdateUserModalVisible(false)}
        footer={null}
        centered
      >
        <Form
          form={updateForm}
          onFinish={handleUpdateUser}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[
                  { required: true, whitespace: true, message: "Please input the first name!" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[
                  { required: true, whitespace: true, message: "Please input the last name!" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: "Please select a role!" }]}
              >
                <Select placeholder="Select a role">
                  <Option value="project_manager">Project Manager</Option>
                  <Option value="field">Field</Option>
                  <Option value="consultant">Consultant</Option>
                  <Option value="subcontractor">Subcontractor</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Phone Number" name="phone_number">
                <Input
                  onKeyDown={handlePhoneNumberKeyDown}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    updateForm.setFieldsValue({ phone_number: formatted });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Vendor" name="vendor_id">
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={filterOption}
              placeholder="Select a vendor"
              options={vendors?.vendors?.map((vendor) => ({
                value: vendor.id,
                label: vendor.name,
              }))}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateMutation.isPending}
            >
              Update
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
